import { MessageCircle, Briefcase } from 'lucide-react';
import type { Conversation } from '../stores/messageStore';

interface Props {
  conversations: Conversation[];
  currentUserId: string;
  selectedId: string | null;
  onSelect: (conv: Conversation) => void;
  loading?: boolean;
}

export default function ConversationList({
  conversations,
  currentUserId,
  selectedId,
  onSelect,
  loading,
}: Props) {
  if (loading) {
    return <div className="p-6 text-sm text-gray-500 text-center">로딩 중...</div>;
  }

  if (conversations.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        <MessageCircle className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        <p className="text-sm">아직 시작된 대화가 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {conversations.map((c) => {
        const isEmployer = c.employer_user_id === currentUserId;
        const myUnread = isEmployer ? c.employer_unread_count : c.user_unread_count;
        const partner = c.partner_name || c.partner_email || '알 수 없음';

        return (
          <button
            key={c.id}
            onClick={() => onSelect(c)}
            className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
              selectedId === c.id ? 'bg-primary/5 border-l-4 border-primary' : ''
            }`}
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="font-semibold text-sm text-gray-900 truncate">{partner}</div>
              {myUnread > 0 && (
                <span className="shrink-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-accent rounded-full">
                  {myUnread > 99 ? '99+' : myUnread}
                </span>
              )}
            </div>
            {c.job_title && (
              <div className="flex items-center gap-1 text-xs text-primary mb-1 truncate">
                <Briefcase className="w-3 h-3 shrink-0" />
                <span className="truncate">{c.job_title}</span>
              </div>
            )}
            <div className="text-xs text-gray-500 truncate">
              {c.last_message_preview || '메시지 없음'}
            </div>
            <div className="text-[10px] text-gray-400 mt-1">
              {formatTime(c.last_message_at)}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const day = 24 * 60 * 60 * 1000;
  if (diff < day && d.getDate() === now.getDate()) {
    return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  }
  if (diff < 7 * day) {
    return d.toLocaleDateString('ko-KR', { weekday: 'short' });
  }
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
}
